import { useCallback, useMemo, useState } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { apiGet } from '../../../generic/Api_Functions'
import useMosInfo from '../common/provider/useMosInfo'
import MosSmartTable from '../common/MosSmartTable'
import CenteredText from '../common/CenteredText'
import WorkerRequiredButton from '../common/WorkerRequiredButton'
import useConsumptionMode from '../common/hooks/useConsumptionMode'
import ConsumptionModeSelector from '../common/ConsumptionModeSelector'
import InstancesDialog from './InstancesDialog'
import LoadDialog from './LoadDialog'

const MACHINE_STORAGE_NOT_FOUND_STATUS = 404

// #39 (Bloque B): loading material into the Machine Storage requires the MACHINE_STORAGE_OPERATE
// capability (S28) — front gating only, the back verifies again fail-secure. `worker`-less state
// keeps the action visible (same criterion as the pre-existing supervisor gating for the Active
// Jobs part actions) so WorkerRequiredButton can still prompt the presence flow first; once a
// worker is logged in, an empty `mosCapabilities` (S30 default) hides it.
export const MACHINE_STORAGE_OPERATE = 'MACHINE_STORAGE_OPERATE'

const PREVIEW_CONTENT = [
  { referenceId: 1, referenceCode: 'REF-A', referenceName: 'Sample reference', available: 70, availableSecondUnit: 12 },
  { referenceId: 2, referenceCode: 'REF-B', referenceName: 'Another reference', available: 30, availableSecondUnit: 5 }
]

// S1: shows the Machine Storage content of the current device as reference + available-quantity
// rows, each offering a "view instances" drill action. The list is paginated server-side: the
// content endpoint returns a Spring Page (`{ content, totalElements, ... }`) and accepts page/size
// (same RequestPagination pattern as rawMaterialsReference/paginated), so the table runs in
// `pageable` mode.
// The content GET also doubles as the 404 detector: a 404 (MACHINE_STORAGE_NOT_FOUND) means the
// machine has no Machine Storage → render an elegant empty state instead of breaking.
const MachineMaterialWidget = ({ preview = false }) => {
  const { t_ } = useSmartTranslation()
  const { device, worker, hasCapability } = useMosInfo()
  const canOperate = worker ? (typeof hasCapability === 'function' && hasCapability(MACHINE_STORAGE_OPERATE)) : true
  // The Machine Storage consumption mode selector (FIFO/EQUILIBRADO/SELECCION) lives only in this
  // widget: the mode is server-persisted per machine and shared with every other widget through
  // MosInfoProvider, so the other widgets just react to it. Changing it is gated by the
  // MACHINE_STORAGE_CHANGE_CONSUMPTION_MODE capability (S7).
  const { mode, canChangeMode, changeMode } = useConsumptionMode()
  const [notFound, setNotFound] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [instancesDialog, setInstancesDialog] = useState({ open: false, referenceId: 0, referenceCode: '' })
  const [loadDialogOpen, setLoadDialogOpen] = useState(false)

  const refreshContent = useCallback(() => setRefreshKey((value) => value + 1), [])

  // Paginated content fetch (Spring Page envelope) that also acts as the 404 detector. Passes the
  // table's own pagination (page/size) through to the endpoint and returns the Page as-is so the
  // pageable table reads `content` + `totalElements`; a plain array (older backend) is wrapped.
  const fetchContent = useCallback(async (requestPagination) => {
    if (preview) {
      return { content: PREVIEW_CONTENT, totalElements: PREVIEW_CONTENT.length }
    }
    if (!device?.id) {
      return { content: [], totalElements: 0 }
    }
    try {
      const result = await apiGet('mos/machineStorage/' + device.id + '/content', requestPagination)
      setNotFound(false)
      return result
    } catch (error) {
      if (error?.status === MACHINE_STORAGE_NOT_FOUND_STATUS) {
        setNotFound(true)
        return { content: [], totalElements: 0 }
      }
      throw error
    }
  }, [device?.id, preview])

  const openInstances = useCallback((row) => {
    setInstancesDialog({ open: true, referenceId: row.referenceId, referenceCode: row.referenceCode })
  }, [])

  const closeInstances = useCallback(() => {
    setInstancesDialog((prev) => ({ ...prev, open: false }))
  }, [])

  const columns = useMemo(() => [
    { name: t_('Reference'), field: 'referenceCode', readOnly: true },
    { name: t_('Name'), field: 'referenceName', readOnly: true },
    { name: t_('Available in machine'), field: 'available', readOnly: true, search: false },
    // The machine stock in the reference's second unit of measure (Sun aggregates it alongside the
    // primary `available`). References with no second unit come back empty (N/A), so the cell is
    // simply blank for them. Reuses the config vocabulary key so the label matches the Raw Material
    // Reference table.
    { name: t_('Quantity available second unit'), field: 'availableSecondUnit', readOnly: true, search: false },
    {
      name: t_('Instances'),
      field: 'actions',
      readOnly: true,
      search: false,
      sortable: false,
      render: (row) => (
        <IconButton aria-label={t_('View instances')} onClick={() => openInstances(row)} size='small'>
          <VisibilityIcon />
        </IconButton>
      )
    }
  ], [t_, openInstances])

  if (!preview && notFound) {
    return (
      <CenteredText>
        <Typography variant='body2' color='text.secondary'>{t_('This machine has no material storage')}</Typography>
      </CenteredText>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
      <InstancesDialog
        open={instancesDialog.open}
        referenceId={instancesDialog.referenceId}
        referenceCode={instancesDialog.referenceCode}
        onClose={closeInstances}
        onChanged={refreshContent}
      />
      <LoadDialog
        open={loadDialogOpen}
        onClose={() => setLoadDialogOpen(false)}
        onSuccess={refreshContent}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, p: 1 }}>
        <ConsumptionModeSelector
          mode={mode}
          onChange={changeMode}
          disabled={!canChangeMode}
          sx={{ justifyContent: 'flex-start' }}
        />
        {canOperate && (
          <WorkerRequiredButton variant='contained' onClick={() => setLoadDialogOpen(true)}>
            {t_('Load material')}
          </WorkerRequiredButton>
        )}
      </Box>
      <Box sx={{ overflow: 'auto', width: '100%', flexGrow: 1 }}>
        <MosSmartTable
          columns={columns}
          dataFetch={fetchContent}
          pageable
          tablePageSize={10}
          forceLoad={refreshKey}
          showSearchHeader
          hideExport
        />
      </Box>
    </Box>
  )
}

export default MachineMaterialWidget
